/**
 * Layout — 主布局（按 docs/ui_sidebar 模板 01 + ui_topbar + ui_scroller + ui_dialog 重构）
 * 结构: NavDrawerHeader(Brand Bar + 搜索) + NavDrawerBody(分组菜单, 滚动条三态)
 *       + 顶栏(面包屑 + 右侧用户头像菜单) + 内容区
 * 特性: 最长前缀高亮 / 搜索过滤+<mark>高亮 / Ctrl+K 聚焦 /
 *       响应式折叠(<1024px 自动 64px 图标 only) / 点击 Brand Bar 弹出「关于系统」Dialog
 * 规范要点:
 *   - 侧栏无 Footer（用户区已按 docs/ui_topbar §3.1 迁移至顶栏右侧）
 *   - NavDrawerBody 滚动条三态（docs/ui_scroller：默认隐藏 / 滚动中显示 / 悬浮显示）
 *   - 「关于系统」Dialog 用 DialogCloseTitle（docs/ui_dialog：右上角 ✕，无底部纯关闭按钮）
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Outlet, useNavigate, useLocation } from 'react-router-dom'
import { useScrollReveal } from '@/hooks/useScrollReveal'
import {
  makeStyles,
  mergeClasses,
  tokens,
  Avatar,
  Text,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Button,
  SearchBox,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogContent,
  Divider,
} from '@fluentui/react-components'
import {
  NavDrawer,
  NavDrawerHeader,
  NavDrawerBody,
  NavItem,
  AppItem,
  NavSectionHeader,
} from '@fluentui/react-nav'
import {
  MailRegular,
  SettingsRegular,
  ArrowExitRegular,
} from '@fluentui/react-icons'
import { useAuthStore } from '@/store/auth'
import { ROUTES } from '@/routes'
import { NAV, NAV_TOP } from '@/nav/schema'
import { useNavVisibility } from '@/nav/useNavVisibility'
import PageBreadcrumb from './PageBreadcrumb'
import { DialogCloseTitle } from './DialogCloseTitle'

// 「关于系统」内容（Brand Bar 点击弹出；CUSTOMIZE 替换为真实信息）
const SYSTEM_ABOUT = {
  name: 'Ming-Mail',
  version: '0.2.0 (2026-08)',
  description:
    'Ming-Mail 是面向企业自托管的邮件服务器管理平台，提供域名、用户、别名、队列、备份、DKIM 与 TLS 证书等完整管理能力，支持 OAuth2 / OIDC 单点登录。',
  copyright: '© 2026 Ming. 保留所有权利。',
}

const useStyles = makeStyles({
  root: {
    display: 'flex',
    height: '100vh',
    width: '100vw',
    overflow: 'hidden',
  },
  navDrawer: {
    height: '100%',
    flexShrink: 0,
    borderRight: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  navSearch: {
    padding: '8px 12px',
  },
  // 侧栏滚动容器：竖向滚动条默认隐藏，滚动中 / 悬浮容器时显示（docs/ui_scroller §4.3）
  navBody: {
    // WebKit/Chromium
    '::-webkit-scrollbar': { width: '8px', height: '8px' },
    '::-webkit-scrollbar-track': { background: 'transparent' },
    '::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '4px' },
    ':hover::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.25)' },
    ':hover::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.04)' },
    // Firefox
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
    ':hover': { scrollbarColor: 'rgba(0,0,0,0.25) transparent' },
  },
  // 滚动进行中：显示滚动条（由 scroll 事件 + debounce 控制）
  navBodyScrolling: {
    '::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.25)' },
    scrollbarColor: 'rgba(0,0,0,0.25) transparent',
  },
  // 内容区滚动容器：同侧栏滚动条三态（docs/ui_scroller §0.5 全站竖向滚动容器）
  contentBody: {
    flex: 1,
    overflowX: 'hidden',
    overflowY: 'auto',
    padding: '20px',
    // WebKit/Chromium
    '::-webkit-scrollbar': { width: '8px', height: '8px' },
    '::-webkit-scrollbar-track': { background: 'transparent' },
    '::-webkit-scrollbar-thumb': { background: 'transparent', borderRadius: '4px' },
    ':hover::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.25)' },
    ':hover::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.04)' },
    // Firefox
    scrollbarWidth: 'thin',
    scrollbarColor: 'transparent transparent',
    ':hover': { scrollbarColor: 'rgba(0,0,0,0.25) transparent' },
  },
  contentBodyScrolling: {
    '::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.25)' },
    scrollbarColor: 'rgba(0,0,0,0.25) transparent',
  },
  main: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    backgroundColor: tokens.colorNeutralBackground2,
  },
  // 顶栏（docs/ui_topbar：面包屑 + 右侧用户区，40px）
  topbar: {
    display: 'flex',
    alignItems: 'center',
    height: '40px',
    minHeight: '40px',
    padding: '0 20px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  topbarLeft: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
  },
  topbarRight: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginLeft: '12px',
  },
  userButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '2px 8px',
    borderRadius: tokens.borderRadiusMedium,
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2Hover,
    },
  },
  userInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    lineHeight: '16px',
  },
  // 折叠态（<1024px 响应式）：图标 only 64px；label 隐藏、图标居中
  navItemCollapsed: {
    justifyContent: 'center',
    padding: '0',
  },
  navItemLabel: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  mark: {
    backgroundColor: tokens.colorBrandBackground2,
    fontWeight: 600,
    borderRadius: '2px',
  },
  // 「关于系统」Dialog 外观（Alert Dialog：点遮罩不关闭，右上角 ✕ 关闭）
  aboutSurface: {
    width: '480px',
    maxWidth: '90vw',
  },
  aboutHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '12px',
  },
  aboutLogo: {
    width: '32px',
    height: '32px',
    color: tokens.colorBrandForeground1,
    flexShrink: 0,
  },
  aboutName: {
    fontWeight: tokens.fontWeightSemibold,
    fontSize: tokens.fontSizeBase500,
  },
  aboutVersion: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  aboutDesc: {
    display: 'block',
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: '20px',
  },
  aboutCopyright: {
    display: 'block',
    marginTop: '8px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
})

/** 响应式折叠：<1024px 图标 only（docs/ui_sidebar §7） */
function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const mql = window.matchMedia(query)
    const handler = (e: MediaQueryListEvent) => setMatches(e.matches)
    mql.addEventListener('change', handler)
    setMatches(mql.matches)
    return () => mql.removeEventListener('change', handler)
  }, [query])
  return matches
}

export default function Layout() {
  const styles = useStyles()
  const navigate = useNavigate()
  const location = useLocation()
  const user = useAuthStore((s) => s.user)
  const logout = useAuthStore((s) => s.logout)

  const [query, setQuery] = useState('')
  const [aboutOpen, setAboutOpen] = useState(false)
  const searchRef = useRef<HTMLInputElement>(null)

  // 滚动条显隐三态（docs/ui_scroller §4.1）：侧栏 + 内容区各一份
  const navScroll = useScrollReveal()
  const contentScroll = useScrollReveal()

  // 响应式折叠（§7）：<1024px 默认折叠
  const collapsed = useMediaQuery('(max-width: 1023px)')

  const handleLogout = useCallback(() => {
    logout()
    navigate(ROUTES.LOGIN, { replace: true })
  }, [logout, navigate])

  // 1) 权限过滤（mailman：登录即 admin，恒全量）
  const visibleGroups = useNavVisibility(NAV)

  // 2) 搜索过滤：label / path / description 模糊匹配
  const q = query.trim().toLowerCase()
  const topMatch =
    !q ||
    NAV_TOP.label.toLowerCase().includes(q) ||
    NAV_TOP.path.toLowerCase().includes(q) ||
    (NAV_TOP.description ?? '').toLowerCase().includes(q)

  const filteredGroups = useMemo(
    () =>
      visibleGroups
        .map((g) => ({
          ...g,
          items: g.items.filter(
            (i) =>
              !q ||
              i.label.toLowerCase().includes(q) ||
              i.path.toLowerCase().includes(q) ||
              (i.description ?? '').toLowerCase().includes(q),
          ),
        }))
        .filter((g) => g.items.length > 0),
    [visibleGroups, q],
  )

  // 3) 当前高亮：最长前缀匹配（§6.3）
  const allNavPaths = [
    NAV_TOP.path,
    ...visibleGroups.flatMap((g) => g.items.map((i) => i.path)),
  ]
  const activeValue =
    allNavPaths
      .filter((p) => location.pathname === p || location.pathname.startsWith(p + '/'))
      .sort((a, b) => b.length - a.length)[0] ?? NAV_TOP.path

  // 4) 快捷键：Ctrl/Cmd+K 聚焦搜索；Esc 清空并失焦（§6.1 / §6.6）
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault()
        searchRef.current?.focus()
      } else if (e.key === 'Escape') {
        setQuery('')
        searchRef.current?.blur()
      }
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [])

  const handleNavigate = useCallback((path: string) => navigate(path), [navigate])

  // 搜索命中高亮：把命中的子串包 <mark>（§6.2）
  const highlight = useCallback(
    (text: string) => {
      if (!q) return text
      const idx = text.toLowerCase().indexOf(q)
      if (idx < 0) return text
      return (
        <>
          {text.slice(0, idx)}
          <mark className={styles.mark}>{text.slice(idx, idx + q.length)}</mark>
          {text.slice(idx + q.length)}
        </>
      )
    },
    [q, styles.mark],
  )

  const renderNavItem = (path: string, label: string, icon: React.ReactElement) => (
    <NavItem
      value={path}
      icon={icon}
      className={collapsed ? styles.navItemCollapsed : undefined}
      onClick={() => handleNavigate(path)}
      aria-label={collapsed ? label : undefined}
      title={collapsed ? label : undefined}
    >
      {collapsed ? undefined : <span className={styles.navItemLabel}>{highlight(label)}</span>}
    </NavItem>
  )

  return (
    <div className={styles.root}>
      <NavDrawer
        open
        type="inline"
        selectedValue={activeValue}
        className={styles.navDrawer}
        style={{ width: collapsed ? '64px' : '260px' }}
        onNavItemSelect={(_, data) => handleNavigate(data.value as string)}
      >
        {/* === Brand Bar（§3.1 ①）：点击弹出「关于系统」Dialog（§6.8）=== */}
        <NavDrawerHeader>
          <AppItem
            icon={<MailRegular />}
            aria-label={`关于系统 ${SYSTEM_ABOUT.name}`}
            onClick={() => setAboutOpen(true)}
          >
            {collapsed ? undefined : SYSTEM_ABOUT.name}
          </AppItem>
          {!collapsed && (
            <div className={styles.navSearch}>
              <SearchBox
                ref={searchRef}
                placeholder="搜索菜单 (Ctrl+K)"
                value={query}
                onChange={(_, data) => setQuery(data.value)}
                size="small"
              />
            </div>
          )}
        </NavDrawerHeader>

        {/* === Menu 区（§3.1 ④）：顶部独立项 + 分组；滚动条三态（docs/ui_scroller）=== */}
        <NavDrawerBody
          className={mergeClasses(styles.navBody, navScroll.scrolling && styles.navBodyScrolling)}
          onScroll={navScroll.onScroll}
        >
          {topMatch && renderNavItem(NAV_TOP.path, NAV_TOP.label, NAV_TOP.icon)}
          {filteredGroups.map((group) => (
            <Fragment key={group.section}>
              {!collapsed && <NavSectionHeader>{group.section}</NavSectionHeader>}
              {group.items.map((n) => renderNavItem(n.path, n.label, n.icon))}
            </Fragment>
          ))}
          {q && filteredGroups.length === 0 && !topMatch && (
            <div style={{ padding: '12px', color: tokens.colorNeutralForeground3, fontSize: '13px' }}>
              未找到匹配项
            </div>
          )}
        </NavDrawerBody>
      </NavDrawer>

      <main className={styles.main}>
        {/* 顶栏（docs/ui_topbar）：回退按钮 + 面包屑 + 右侧用户头像菜单 */}
        <div className={styles.topbar}>
          <div className={styles.topbarLeft}>
            <PageBreadcrumb />
          </div>
          <div className={styles.topbarRight}>
            {user ? (
              <Menu>
                <MenuTrigger disableButtonEnhancement>
                  <Button appearance="transparent" className={styles.userButton}>
                    <Avatar name={user.username || '?'} size={28} color="brand" />
                    <Text block style={{ fontSize: '13px', fontWeight: 600 }}>
                      {user.username}
                    </Text>
                  </Button>
                </MenuTrigger>
                <MenuPopover>
                  <MenuList>
                    <MenuItem icon={<SettingsRegular />} onClick={() => navigate(ROUTES.SETTINGS)}>
                      设置
                    </MenuItem>
                    <MenuItem icon={<ArrowExitRegular />} onClick={handleLogout}>
                      退出登录
                    </MenuItem>
                  </MenuList>
                </MenuPopover>
              </Menu>
            ) : (
              <Button
                appearance="primary"
                onClick={() => navigate(ROUTES.LOGIN)}
              >
                登录
              </Button>
            )}
          </div>
        </div>
        <div
          className={mergeClasses(styles.contentBody, contentScroll.scrolling && styles.contentBodyScrolling)}
          onScroll={contentScroll.onScroll}
        >
          <Outlet />
        </div>
      </main>

      {/* 关于系统：侧栏 LOGO 点击触发（Alert Dialog，点遮罩不关闭，右上角 ✕ 关闭，docs/ui_dialog） */}
      <Dialog open={aboutOpen} onOpenChange={(_, d) => setAboutOpen(d.open)} modalType="alert">
        <DialogSurface className={styles.aboutSurface}>
          <DialogBody>
            <DialogCloseTitle
              title={`关于 ${SYSTEM_ABOUT.name}`}
              onClose={() => setAboutOpen(false)}
            />
            <DialogContent>
              <div className={styles.aboutHeader}>
                <MailRegular className={styles.aboutLogo} />
                <div>
                  <div className={styles.aboutName}>{SYSTEM_ABOUT.name}</div>
                  <div className={styles.aboutVersion}>版本 {SYSTEM_ABOUT.version}</div>
                </div>
              </div>
              <Text className={styles.aboutDesc}>{SYSTEM_ABOUT.description}</Text>
              <Divider />
              <Text className={styles.aboutCopyright}>{SYSTEM_ABOUT.copyright}</Text>
            </DialogContent>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </div>
  )
}
